import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Switch,
} from '@mui/material';
import { Close as CloseIcon, CalendarToday as CalendarIcon, Visibility as VisibilityIcon, PictureAsPdf as PdfIcon, Delete as DeleteIcon, Edit as EditIcon, CheckCircle as CheckCircleIcon, Info as InfoIcon } from '@mui/icons-material';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, getDoc, orderBy, serverTimestamp, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { normalizeDateFormat, formatShortDate, getShortDayName, getDayName, generateDaysBetweenDates } from '../../utils/dateUtils';

// Utilisation de la fonction normalizeDateFormat importée depuis dateUtils.js

// Fonction pour vérifier si une date est dans un ensemble de dates normalisées
const isDateInSet = (dateStr, dateSet) => {
  if (!dateSet || !(dateSet instanceof Set) || dateSet.size === 0) return false;
  
  // Normaliser la date à vérifier
  const normalizedDateStr = normalizeDateFormat(dateStr);
  if (!normalizedDateStr) return false;
  
  // Vérifier directement si la date normalisée est dans l'ensemble
  if (dateSet.has(normalizedDateStr)) return true;
  
  // Si la date n'est pas trouvée directement, vérifier avec normalisation
  return Array.from(dateSet).some(d => {
    const normalizedD = normalizeDateFormat(d);
    return normalizedD === normalizedDateStr;
  });
};

// Fonctions utilitaires
const formatDate = (date) => {
  if (!date) return 'Date non définie';
  
  // Si c'est un timestamp Firestore (avec toDate)
  if (date && typeof date.toDate === 'function') {
    return date.toDate().toLocaleDateString('fr-FR');
  }
  
  // Si c'est déjà un objet Date
  if (date instanceof Date) {
    return date.toLocaleDateString('fr-FR');
  }
  
  // Sinon, essayer de convertir en Date
  try {
    return new Date(date).toLocaleDateString('fr-FR');
  } catch (error) {
    console.error('Erreur de formatage de date:', error);
    return 'Date invalide';
  }
};

// Les fonctions utilitaires ont été déplacées dans src/utils/dateUtils.js

const organizeDataForTable = (permanences, personnel) => {
  // Récupérer toutes les dates uniques et les convertir au format YYYY-MM-DD
  const allDates = new Set();
  permanences.forEach(p => p.days.forEach(d => {
    // Normaliser la date au format YYYY-MM-DD avec notre fonction utilitaire
    const normalizedDate = normalizeDateFormat(d);
    allDates.add(normalizedDate);
  }));
  const sortedDates = Array.from(allDates).sort();

  // Récupérer tout le personnel (même ceux sans permanence)
  const personnelMap = new Map();
  
  // D'abord, ajouter tout le personnel disponible
  personnel.forEach(person => {
    personnelMap.set(person.id, {
      person,
      dates: new Array(sortedDates.length).fill(false)
    });
  });
  
  // Puis ajouter le personnel des permanences (au cas où il y aurait des IDs non trouvés)
  permanences.forEach(p => {
    p.personnel.forEach(personId => {
      if (!personnelMap.has(personId)) {
        const person = personnel.find(p => p.id === personId);
        if (person) {
          personnelMap.set(personId, {
            person,
            dates: new Array(sortedDates.length).fill(false)
          });
        }
      }
    });
  });

  // Marquer les présences
  permanences.forEach(p => {
    p.personnel.forEach(personId => {
      const personRow = personnelMap.get(personId);
      if (personRow) {
        p.days.forEach(day => {
          // Normaliser la date au format YYYY-MM-DD avec notre fonction utilitaire
          const normalizedDay = normalizeDateFormat(day);
          const dateIndex = sortedDates.indexOf(normalizedDay);
          if (dateIndex !== -1) {
            personRow.dates[dateIndex] = true;
          } else {
          }
        });
      }
    });
  });

  // Trier le personnel par ordre alphabétique (nom puis prénom)
  const sortedPersonnel = Array.from(personnelMap.values()).sort((a, b) => {
    const lastNameComparison = a.person.lastName.localeCompare(b.person.lastName, 'fr');
    if (lastNameComparison !== 0) return lastNameComparison;
    return a.person.firstName.localeCompare(b.person.firstName, 'fr');
  });

  return {
    dates: sortedDates,
    personnel: sortedPersonnel
  };
};

const PdfContent = React.forwardRef(({ periodName, permanences, personnel, userData, observations, establishmentName, anneeScolaire, gestionnaire, principal, responsableEncadrement, logo }, ref) => {
  // Organiser les données une seule fois pour éviter les calculs répétés
  const organizedData = organizeDataForTable(permanences, personnel);
  
  // Utiliser les observations passées en paramètre ou les récupérer des permanences
  const observationsText = observations || (permanences && permanences.length > 0 ? permanences[0].observations || '' : '');
  
  // Détecter les changements de mois pour ajouter une démarcation
  const monthBoundaries = [];
  if (organizedData.dates.length > 1) {
    for (let i = 1; i < organizedData.dates.length; i++) {
      const prevDate = new Date(organizedData.dates[i-1]);
      const currDate = new Date(organizedData.dates[i]);
      if (prevDate.getMonth() !== currDate.getMonth()) {
        monthBoundaries.push(i);
      }
    }
  }
  
  const dateCount = organizedData.dates.length;

  // Tailles de police adaptées au nombre de colonnes de dates
  // Colonnes fixes (Nom+Service) = ~30 % → dates ont ~70 % de 756px ≈ 529px
  const dateFontSize  = Math.max(5,  Math.min(10, Math.floor(529 / Math.max(1, dateCount * 2.8))));
  const bodyFontSize  = Math.max(7,  Math.min(12, dateFontSize * 1.2));
  const headerFontSize = Math.max(8, Math.min(14, dateFontSize * 1.4));

  // Diviser le personnel en pages si le nombre dépasse 20 personnes
  const personnelPages = [];
  const personnelPerPage = 21;

  if (organizedData.personnel.length > personnelPerPage) {
    for (let i = 0; i < organizedData.personnel.length; i += personnelPerPage) {
      personnelPages.push(organizedData.personnel.slice(i, i + personnelPerPage));
    }
  } else {
    personnelPages.push(organizedData.personnel);
  }
  
  // Nombre total de pages pour affichage
  const totalPages = personnelPages.length;
  
  return (
    <div ref={ref} className="pdf-container">
      {/* Générer une page pour chaque groupe de personnel */}
      {personnelPages.map((personnelPage, pageIndex) => (
        <div 
          key={`page-${pageIndex}`}
          className="pdf-page"
          style={{ 
            width: '210mm', // Largeur A4 standard
            height: '297mm', // Hauteur A4 standard
            margin: pageIndex > 0 ? '20px auto 0' : '0 auto', // Espacement entre les pages pour l'affichage
            padding: '5mm', // Marges réduites pour maximiser l'espace
            fontFamily: 'Arial, sans-serif',
            fontSize: '11px', // Taille de police augmentée significativement
            boxSizing: 'border-box',
            backgroundColor: '#ffffff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            pageBreakAfter: 'always' // Force un saut de page lors de l'impression
          }}
        >
      {/* En-tête avec numéro de page */}
      <div style={{ 
        marginBottom: '5px',
        borderBottom: '2px solid #1976d2',
        paddingBottom: '3px',
        position: 'relative'
      }}>
        {/* Logo à gauche */}
        {logo && logo.startsWith('data:image/') && (
          <div style={{ position: 'absolute', left: 0, top: 0 }}>
            <img 
              src={logo} 
              alt="Logo de l'établissement" 
              style={{ 
                maxHeight: '60px', 
                maxWidth: '100px', 
                objectFit: 'contain'
              }} 
            />
          </div>
        )}
        
        {/* Logo à droite */}
        {logo && logo.startsWith('data:image/') && (
          <div style={{ position: 'absolute', right: 0, top: 0 }}>
            <img 
              src={logo} 
              alt="Logo de l'établissement" 
              style={{ 
                maxHeight: '60px', 
                maxWidth: '100px', 
                objectFit: 'contain'
              }} 
            />
          </div>
        )}
        <div style={{ textAlign: 'center', paddingTop: '40px' }}>
          <h1 style={{ fontSize: '16px', margin: '0 0 3px 0', fontWeight: 'bold', color: '#1976d2' }}>
            {establishmentName || 'Nom de l\'établissement'}
          </h1>
          <h2 style={{ fontSize: '14px', margin: '0 0 3px 0', fontWeight: 'bold' }}>
            Planning des permanences - {periodName}
          </h2>
          <h3 style={{ fontSize: '12px', margin: '0', fontWeight: 'normal' }}>
            Année scolaire: {anneeScolaire || '2024-2025'} - Page {pageIndex + 1}/{personnelPages.length}
          </h3>
        </div>
      </div>
      
      {/* Pied de page */}
      {/* L'ancien pied de page centré a été supprimé */}

      <div style={{ overflow: 'hidden', width: '100%', flex: '1 0 auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: `${bodyFontSize}px`, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '2px', backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '18%', fontSize: `${headerFontSize}px` }}>
                Personnel
              </th>
              <th style={{ border: '1px solid #ddd', padding: '2px', backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '12%', fontSize: `${headerFontSize}px` }}>
                Service
              </th>
              {organizedData.dates.map((date, index) => {
                const dateObj = new Date(date);
                const isMonthBoundary = monthBoundaries.includes(index);
                return (
                  <th
                    key={`day-${date}`}
                    style={{
                      border: '1px solid #ddd',
                      padding: '1px',
                      backgroundColor: '#f5f5f5',
                      textAlign: 'center',
                      fontSize: `${dateFontSize}px`,
                      overflow: 'hidden',
                      borderLeft: isMonthBoundary ? '2px solid #1976d2' : '1px solid #ddd'
                    }}
                  >
                    {getShortDayName(dateObj)}
                  </th>
                );
              })}
            </tr>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '2px', backgroundColor: '#e6f2ff', width: '18%' }}></th>
              <th style={{ border: '1px solid #ddd', padding: '2px', backgroundColor: '#e6f2ff', width: '12%' }}></th>
              {organizedData.dates.map((date, index) => {
                const dateObj = new Date(date);
                const isMonthBoundary = monthBoundaries.includes(index);
                return (
                  <th
                    key={`date-${date}`}
                    style={{
                      border: '1px solid #ddd',
                      padding: '1px',
                      backgroundColor: '#e6f2ff',
                      textAlign: 'center',
                      fontSize: `${dateFontSize}px`,
                      fontWeight: 'bold',
                      overflow: 'hidden',
                      borderLeft: isMonthBoundary ? '2px solid #1976d2' : '1px solid #ddd'
                    }}
                  >
                    {formatShortDate(dateObj)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {personnelPage.map((row) => (
              <tr key={row.person.id}>
                <td style={{ border: '1px solid #ddd', padding: '2px', fontSize: `${bodyFontSize}px`, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <div>{row.person.lastName} {row.person.firstName}</div>
                  {(() => {
                    const personPermanence = permanences.find(p => p.personnel.includes(row.person.id));
                    if (personPermanence?.startTime && personPermanence?.endTime) {
                      return (
                        <div style={{ fontSize: `${Math.max(5, dateFontSize - 1)}px`, color: '#666', marginTop: '1px' }}>
                          {personPermanence.startTime} - {personPermanence.endTime}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '2px', fontSize: `${Math.max(6, bodyFontSize - 1)}px`, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.person.service || row.person.department || '-'}</td>
                {row.dates.map((isPresent, index) => {
                  const isMonthBoundary = monthBoundaries.includes(index);
                  return (
                    <td
                      key={index}
                      style={{
                        border: '1px solid #ddd',
                        padding: '1px',
                        textAlign: 'center',
                        fontWeight: isPresent ? 'bold' : 'normal',
                        fontSize: `${dateFontSize}px`,
                        borderLeft: isMonthBoundary ? '2px solid #1976d2' : '1px solid #ddd'
                      }}
                    >
                      {isPresent ? '✓' : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Zone d'observations - affichée uniquement si des observations existent */}
      {observationsText && (
        <div style={{ marginTop: '5px', marginBottom: '5px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '3px', fontSize: '11px' }}>Observations:</div>
          <div style={{ 
            border: '1px solid #ddd', 
            padding: '3px', 
            minHeight: '25px',
            fontSize: '10px',
            boxSizing: 'border-box',
            overflowY: 'auto'
          }}>
            {observationsText}
          </div>
        </div>
      )}

      {/* Zone des signatures - avec cases agrandies et uniformes */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        marginTop: '8px',
        fontSize: '11px',
        width: '100%'
      }}>
        <div style={{ flex: 1, marginRight: '10px' }}>
          <div style={{ marginBottom: '4px', fontWeight: 'bold', fontSize: '12px' }}>Le Gestionnaire</div>
          <div style={{ marginBottom: '4px', fontSize: '11px' }}>{gestionnaire || userData?.gestionnaire || ''}</div>
          <div style={{ 
            border: '2px solid #000', 
            height: '40px',
            width: '100%'
          }}></div>
        </div>
        <div style={{ flex: 1, margin: '0 10px' }}>
          <div style={{ marginBottom: '4px', fontWeight: 'bold', fontSize: '12px' }}>Le Proviseur</div>
          <div style={{ marginBottom: '4px', fontSize: '11px' }}>{principal || userData?.principal || ''}</div>
          <div style={{ 
            border: '2px solid #000', 
            height: '40px',
            width: '100%'
          }}></div>
        </div>
        <div style={{ flex: 1, marginLeft: '10px' }}>
          <div style={{ marginBottom: '4px', fontWeight: 'bold', fontSize: '12px' }}>L'Encadrant</div>
          <div style={{ marginBottom: '4px', fontSize: '11px' }}>{responsableEncadrement || userData?.responsableEncadrement || ''}</div>
          <div style={{ 
            border: '2px solid #000', 
            height: '40px',
            width: '100%'
          }}></div>
        </div>
      </div>
      
      {/* Pied de page avec le nom du site */}
      <div style={{ marginTop: '10px', fontSize: '10px', textAlign: 'right', color: '#666', position: 'absolute', bottom: '5px', right: '5px' }}>
        Edugestion.softmarket.io
      </div>
        </div>
      ))}
    </div>
  );
});

// Composant principal de gestion des permanences
const ScheduleManagerComponent = () => {
  // État pour suivre le mois actuellement affiché
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);

  // Liste des jours fériés français avec leurs couleurs
  const joursFeries = {
    "01-01": { nom: "Jour de l'an", couleur: '#ffebee' },
    "04-10": { nom: "Lundi de Pâques", couleur: '#fff3e0' },
    "05-01": { nom: "Fête du Travail", couleur: '#e8f5e9' },
    "05-08": { nom: "Victoire 1945", couleur: '#e3f2fd' },
    "05-18": { nom: "Ascension", couleur: '#f3e5f5' },
    "05-29": { nom: "Lundi de Pentecôte", couleur: '#fce4ec' },
    "07-14": { nom: "Fête Nationale", couleur: '#e8eaf6' },
    "08-15": { nom: "Assomption", couleur: '#e0f7fa' },
    "11-01": { nom: "Toussaint", couleur: '#fff3e0' },
    "11-11": { nom: "Armistice", couleur: '#fbe9e7' },
    "12-25": { nom: "Noël", couleur: '#ffebee' }
  };

  // Fonction pour vérifier si une date est un jour férié
  const isJourFerie = (date) => {
    const dateStr = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return joursFeries[dateStr];
  };

  const generateDaysBetweenDates = (startDate, endDate) => {
    if (!startDate || !endDate) {
      console.error('Dates invalides dans generateDaysBetweenDates:', { startDate, endDate });
      return [];
    }
    
    try {
      const dates = [];
      
      // S'assurer que nous travaillons avec des objets Date
      let currentDate, lastDate;
      
      // Traitement sécurisé de la date de début
      if (startDate instanceof Date) {
        currentDate = new Date(startDate.getTime()); // Cloner la date
      } else if (typeof startDate === 'string' || typeof startDate === 'number') {
        currentDate = new Date(startDate);
      } else if (startDate && typeof startDate.toDate === 'function') {
        currentDate = startDate.toDate();
      } else {
        console.error('Format de date de début non reconnu:', startDate);
        return [];
      }
      
      // Traitement sécurisé de la date de fin
      if (endDate instanceof Date) {
        lastDate = new Date(endDate.getTime()); // Cloner la date
      } else if (typeof endDate === 'string' || typeof endDate === 'number') {
        lastDate = new Date(endDate);
      } else if (endDate && typeof endDate.toDate === 'function') {
        lastDate = endDate.toDate();
      } else {
        console.error('Format de date de fin non reconnu:', endDate);
        return [];
      }
      
      // Vérifier que les dates sont valides
      if (isNaN(currentDate.getTime()) || isNaN(lastDate.getTime())) {
        console.error('Dates invalides après conversion dans generateDaysBetweenDates');
        return [];
      }
      
      // Réinitialiser l'heure pour éviter les problèmes de comparaison
      currentDate.setHours(0, 0, 0, 0);
      lastDate.setHours(0, 0, 0, 0);
      
      // Ajouter un jour à la date de fin pour inclure cette date dans la plage
      lastDate.setDate(lastDate.getDate() + 1);
      
      // Limiter le nombre de jours à 60 pour éviter les boucles infinies
      let safetyCounter = 0;
      const MAX_DAYS = 60;
      
      while (currentDate < lastDate && safetyCounter < MAX_DAYS) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
        safetyCounter++;
      }
      
      return dates;
    } catch (error) {
      console.error('Erreur dans generateDaysBetweenDates:', error);
      return [];
    }
  };

  const { userData } = useAuth();
  
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedPeriodDates, setSelectedPeriodDates] = useState(null);
  const [periodDays, setPeriodDays] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState([]);
  const [personnelSelections, setPersonnelSelections] = useState(new Map());
  const [searchTerm, setSearchTerm] = useState('');  // État pour stocker le terme de recherche du personnel
  const [observations, setObservations] = useState('');
  const [globalTimeSchedule, setGlobalTimeSchedule] = useState({ startTime: '08:00', endTime: '16:00' });
  const [useGlobalTimeSchedule, setUseGlobalTimeSchedule] = useState(true);
  const [personnelTimeSchedules, setPersonnelTimeSchedules] = useState(new Map());
  const [vacationPeriods, setVacationPeriods] = useState([]);
  const [currentYear, setCurrentYear] = useState('2024-2025');
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [permanences, setPermanences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, periodId: null, periodName: '' });
  const [pdfComponentRef] = useState(() => React.createRef());
  const [selectedPeriodForPdf, setSelectedPeriodForPdf] = useState(null);
  const [establishmentInfo, setEstablishmentInfo] = useState({
    name: '',
    anneeScolaire: '',
    gestionnaire: '',
    principal: '',
    responsableEncadrement: ''
  });
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedPermanenceToEdit, setSelectedPermanenceToEdit] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [visibleToPersonnel, setVisibleToPersonnel] = useState(true);
  const [isEditingWholePeriod, setIsEditingWholePeriod] = useState(false); // Flag pour distinguer modification générale vs individuelle

  useEffect(() => {
    const fetchEstablishmentInfo = async () => {
      if (userData?.establishmentId) {
        try {
          const establishmentRef = doc(db, 'establishments', userData.establishmentId);
          const establishmentSnap = await getDoc(establishmentRef);
          
          if (establishmentSnap.exists()) {
            const establishmentData = establishmentSnap.data();
            setEstablishmentInfo(establishmentData);
          }
        } catch (error) {
          setError("Erreur lors de la récupération des informations de l'établissement");
        }
      }
    };

    fetchEstablishmentInfo();
  }, []);

  useEffect(() => {
    if (userData?.establishmentId) {
      fetchVacationPeriods();
      fetchPersonnel();
      fetchPermanences();
    }
  }, [userData]);

  const fetchVacationPeriods = async () => {
    try {
      const vacancesRef = collection(db, 'establishments', userData.establishmentId, 'vacances');
      const querySnapshot = await getDocs(vacancesRef);
      const periods = querySnapshot.docs.map(doc => {
        const data = doc.data();
        let debutDate = null;
        let finDate = null;
        
        // Conversion sécurisée des timestamps en objets Date
        try {
          if (data.debut && typeof data.debut.toDate === 'function') {
            debutDate = data.debut.toDate();
          } else if (data.debut) {
            debutDate = new Date(data.debut);
          }
        } catch (err) {
          console.error('Erreur lors de la conversion de la date de début:', err);
        }
        
        try {
          if (data.fin && typeof data.fin.toDate === 'function') {
            finDate = data.fin.toDate();
          } else if (data.fin) {
            finDate = new Date(data.fin);
          }
        } catch (err) {
          console.error('Erreur lors de la conversion de la date de fin:', err);
        }
        
        // Vérifier que les dates sont valides
        if (debutDate && isNaN(debutDate.getTime())) {
          console.error('Date de début invalide pour la période:', doc.id);
          debutDate = null;
        }
        
        if (finDate && isNaN(finDate.getTime())) {
          console.error('Date de fin invalide pour la période:', doc.id);
          finDate = null;
        }
        
        return {
          id: doc.id,
          ...data,
          // Conserver les champs originaux pour la compatibilité
          debut: data.debut,
          fin: data.fin,
          // Ajouter des champs convertis pour l'affichage
          debutDate: debutDate,
          finDate: finDate,
          // Ajouter des chaînes formatées pour l'affichage
          debutStr: debutDate ? debutDate.toLocaleDateString('fr-FR') : 'Date non définie',
          finStr: finDate ? finDate.toLocaleDateString('fr-FR') : 'Date non définie'
        };
      });
      
      setVacationPeriods(periods);
    } catch (error) {
      console.error('Erreur dans fetchVacationPeriods:', error);
      setError('Erreur lors du chargement des périodes de vacances');
    }
  };

  const fetchPersonnel = async () => {
    try {
      if (!userData?.establishmentId) {
        setError("!!! Erreur: establishmentId manquant");
        return;
      }

      const usersRef = collection(db, 'users');
      
      // Récupérer le personnel régulier
      const personnelQuery = query(
        usersRef, 
        where('establishmentId', '==', userData.establishmentId),
        where('role', '==', 'personnel'),
        where('status', '==', 'actif')
      );
      
      const personnelSnapshot = await getDocs(personnelQuery);
      
      // Récupérer les chefs de service
      const chefsQuery = query(
        usersRef, 
        where('establishmentId', '==', userData.establishmentId),
        where('role', '==', 'chefService'),
        where('status', '==', 'actif')
      );
      
      const chefsSnapshot = await getDocs(chefsQuery);
      
      if (personnelSnapshot.empty && chefsSnapshot.empty) {
        setError("!!! Aucun personnel ou chef de service trouvé pour cet établissement");
      }

      const personnelList = [];
      
      // Ajouter le personnel régulier
      personnelSnapshot.forEach(doc => {
        const data = doc.data();
        personnelList.push({
          id: doc.id,
          ...data,
          isChefService: false
        });
      });
      
      // Ajouter les chefs de service
      chefsSnapshot.forEach(doc => {
        const data = doc.data();
        personnelList.push({
          id: doc.id,
          ...data,
          isChefService: true
        });
      });
      
      // Trier le personnel par ordre alphabétique (nom puis prénom)
      personnelList.sort((a, b) => {
        const lastNameA = (a.lastName || '').toLowerCase();
        const lastNameB = (b.lastName || '').toLowerCase();
        
        if (lastNameA < lastNameB) return -1;
        if (lastNameA > lastNameB) return 1;
        
        const firstNameA = (a.firstName || '').toLowerCase();
        const firstNameB = (b.firstName || '').toLowerCase();
        
        if (firstNameA < firstNameB) return -1;
        if (firstNameA > firstNameB) return 1;
        
        return 0;
      });
      
      
      setPersonnel(personnelList);
      
    } catch (error) {
      console.error('Erreur dans fetchPersonnel:', error);
      setError('Erreur lors de la récupération du personnel');
    }
  };
  
  // Les chefs de service sont maintenant inclus dans la liste du personnel

  // Cache pour stocker les données des périodes et du personnel
  const [periodsCache, setPeriodsCache] = useState(new Map());
  const [personnelCache, setPersonnelCache] = useState(new Map());

  const fetchPermanences = async () => {
    try {
      setLoading(true);
      const permanencesRef = collection(db, 'permanences');
      const q = query(
        permanencesRef,
        where('establishmentId', '==', userData.establishmentId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      // Collecter tous les IDs de périodes et de personnel nécessaires
      const periodIds = new Set();
      const personnelIds = new Set();
      
      querySnapshot.docs.forEach(doc => {
        const permanence = doc.data();
        periodIds.add(permanence.period);
        permanence.personnel.forEach(personId => personnelIds.add(personId));
      });
      
      // Récupérer toutes les périodes manquantes en une seule fois
      const newPeriodsCache = new Map(periodsCache);
      const periodPromises = [];
      
      for (const periodId of periodIds) {
        if (!newPeriodsCache.has(periodId)) {
          periodPromises.push(
            getDoc(doc(db, 'establishments', userData.establishmentId, 'vacances', periodId))
              .then(doc => {
                if (doc.exists()) {
                  newPeriodsCache.set(periodId, doc.data());
                } else {
                  newPeriodsCache.set(periodId, { nom: 'Période inconnue' });
                }
              })
          );
        }
      }
      
      // Récupérer tout le personnel manquant en une seule fois
      const newPersonnelCache = new Map(personnelCache);
      const personnelPromises = [];
      
      for (const personId of personnelIds) {
        if (!newPersonnelCache.has(personId)) {
          personnelPromises.push(
            getDoc(doc(db, 'users', personId))
              .then(doc => {
                if (doc.exists()) {
                  newPersonnelCache.set(personId, { id: doc.id, ...doc.data() });
                } else {
                  newPersonnelCache.set(personId, null);
                }
              })
          );
        }
      }
      
      // Attendre que toutes les requêtes soient terminées
      await Promise.all([...periodPromises, ...personnelPromises]);
      
      // Mettre à jour les caches
      setPeriodsCache(newPeriodsCache);
      setPersonnelCache(newPersonnelCache);
      
      // Construire la liste des permanences avec les données en cache
      const permanencesList = querySnapshot.docs
        .map(docSnapshot => {
          const permanence = docSnapshot.data();
          const periodData = newPeriodsCache.get(permanence.period);
          const personnelData = permanence.personnel
            .map(personId => newPersonnelCache.get(personId))
            .filter(p => p !== null);
          
          return {
            id: docSnapshot.id,
            ...permanence,
            periodName: periodData?.nom || 'Période inconnue',
            personnelDetails: personnelData
          };
        })
        .filter(permanence => {
          // Filtrer les permanences invalides
          return permanence.days && 
                 permanence.days.length > 0 && 
                 permanence.personnel && 
                 permanence.personnel.length > 0 &&
                 permanence.period; // S'assurer qu'il y a une période valide
        });
      
      setPermanences(permanencesList);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement des permanences:", error);
      setError("Erreur lors du chargement des permanences");
      setLoading(false);
    }
  };

  // Fonction pour grouper les permanences par période
  const groupPermanencesByPeriod = (permanences) => {
    const groups = new Map();
    
    // Grouper seulement les permanences existantes par période
    permanences.forEach(permanence => {
      if (!groups.has(permanence.period)) {
        groups.set(permanence.period, []);
      }
      groups.get(permanence.period).push(permanence);
    });
    
    return Array.from(groups.entries()).map(([periodId, perms]) => ({
      periodId,
      periodName: vacationPeriods.find(p => p.id === periodId)?.nom || 'Période inconnue',
      permanences: perms
    }));
  };

  const handlePrint = async () => {
    try {
      // Vérifier que les données nécessaires sont disponibles
      if (!pdfComponentRef.current || !selectedPeriodForPdf) {
        console.error('pdfComponentRef ou selectedPeriodForPdf est null');
        showSnackbar('Erreur: Données de période manquantes', 'error');
        return;
      }

      setLoading(true);
      showSnackbar('Génération du PDF en cours...', 'info');

      // Attendre plus longtemps que le composant PDF soit complètement rendu
      // Un délai plus long permet de s'assurer que le DOM est complètement prêt
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Créer un nouveau document PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true, // Compression pour réduire la taille du PDF
        hotfixes: ['px_scaling'] // Correction pour le scaling des pixels
      });
      
      // Approche alternative pour capturer toutes les pages
      // Au lieu d'utiliser querySelectorAll, nous allons capturer directement le contenu
      // du conteneur principal et le diviser manuellement en pages
      
      // D'abord, vérifions si le conteneur principal existe
      if (!pdfComponentRef.current) {
        console.error('Le conteneur PDF est null');
        showSnackbar('Erreur: Impossible de générer le PDF', 'error');
        return;
      }
      
      // Obtenir toutes les pages directement à partir du DOM
      // Cette méthode est plus fiable que d'utiliser querySelectorAll
      const pdfContainer = pdfComponentRef.current;
      const pageElements = Array.from(pdfContainer.children).filter(el => 
        el.className && el.className.includes('pdf-page')
      );
      
      // Vérifier si des pages ont été trouvées
      if (!pageElements || pageElements.length === 0) {
        console.error('Aucune page trouvée dans le composant PDF');
        
        // Si nous ne trouvons pas de pages avec la classe, essayons de capturer tout le conteneur
        try {
          const canvas = await html2canvas(pdfContainer, {
            scale: 2,
            backgroundColor: '#ffffff',
            logging: true, // Activer les logs pour déboguer
            useCORS: true,
            allowTaint: true
          });
          
          const imgData = canvas.toDataURL('image/png');
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          
          // Ajuster pour tenir sur une page A4
          const margin = 2;
          pdf.addImage(imgData, 'PNG', margin, margin, pageWidth - (margin * 2), pageHeight - (margin * 2));
          
          // Enregistrer le PDF même si nous avons dû utiliser la méthode de secours
          const fileName = selectedPeriodForPdf && selectedPeriodForPdf.periodName 
            ? `permanences_${selectedPeriodForPdf.periodName.replace(/\s+/g, '_')}.pdf`
            : 'permanences.pdf';
          
          pdf.save(fileName);
          showSnackbar('PDF généré avec succès (méthode alternative)', 'success');
          return;
        } catch (fallbackError) {
          console.error('Erreur lors de la tentative de secours:', fallbackError);
          showSnackbar('Erreur lors de la génération du PDF', 'error');
          return;
        }
      }
      
      // Pour chaque page, créer une image et l'ajouter au PDF
      const pageCount = pageElements.length;
      
      for (let i = 0; i < pageCount; i++) {
        const pageElement = pageElements[i];
        
        // Ajouter une nouvelle page pour chaque page après la première
        if (i > 0) {
          pdf.addPage();
        }
        
        try {
          // Capturer la page actuelle
          const canvas = await html2canvas(pageElement, {
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false,
            useCORS: true,
            allowTaint: true,
            width: 210 * 3.78, // 210mm en pixels (3.78 pixels/mm)
            height: 297 * 3.78 // 297mm en pixels
          });
          
          const imgData = canvas.toDataURL('image/png');
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          
          // Ajuster les dimensions pour que l'image tienne parfaitement sur la page A4
          const margin = 2;
          const imgWidth = pageWidth - (margin * 2);
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          // Vérifier si la hauteur calculée dépasse la hauteur de la page
          if (imgHeight <= pageHeight - (margin * 2)) {
            // L'image tient en hauteur, on la centre verticalement
            const yPosition = (pageHeight - imgHeight) / 2;
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
          } else {
            // L'image est trop haute, on la redimensionne pour qu'elle tienne en hauteur
            const adjustedHeight = pageHeight - (margin * 2);
            const adjustedWidth = (canvas.width * adjustedHeight) / canvas.height;
            const xPosition = (pageWidth - adjustedWidth) / 2;
            pdf.addImage(imgData, 'PNG', xPosition, margin, adjustedWidth, adjustedHeight);
          }
        } catch (pageError) {
          console.error(`Erreur lors de la capture de la page ${i+1}:`, pageError);
          // Continuer avec les autres pages même si une page échoue
        }
      }
      
      // S'assurer que selectedPeriodForPdf existe et a une propriété periodName
      const fileName = selectedPeriodForPdf && selectedPeriodForPdf.periodName 
        ? `permanences_${selectedPeriodForPdf.periodName.replace(/\s+/g, '_')}.pdf`
        : 'permanences.pdf';
      
      // Enregistrer le PDF
      pdf.save(fileName);
      showSnackbar('PDF généré avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      showSnackbar('Erreur lors de la génération du PDF', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDeletePermanence = async (periodId) => {
    try {
      setLoading(true);
      
      // Récupérer toutes les permanences liées à cette période
      const permanencesRef = collection(db, 'permanences');
      const q = query(
        permanencesRef,
        where('establishmentId', '==', userData.establishmentId),
        where('period', '==', periodId)
      );
      const querySnapshot = await getDocs(q);
      
      // Supprimer chaque permanence
      const deletePromises = querySnapshot.docs.map(docSnapshot => 
        deleteDoc(doc(db, 'permanences', docSnapshot.id))
      );
      
      await Promise.all(deletePromises);
      
      // Rafraîchir la liste des permanences
      await fetchPermanences();
      
      setLoading(false);
      showSnackbar(`Les permanences de la période ont été supprimées avec succès`, 'success');
      setDeleteConfirmDialog({ open: false, periodId: null, periodName: '' });
    } catch (error) {
      console.error('Erreur lors de la suppression des permanences:', error);
      setLoading(false);
      showSnackbar(`Erreur lors de la suppression des permanences: ${error.message}`, 'error');
      setDeleteConfirmDialog({ open: false, periodId: null, periodName: '' });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleDateSelection = (personId, dateStr) => {
    // La date est déjà normalisée quand elle est passée à cette fonction
    const normalizedDateStr = dateStr;
    
    setPersonnelSelections(prev => {
      const newSelections = new Map(prev);
      const personDates = newSelections.get(personId) || new Set();
      
      // Vérifier si la date existe déjà (en tenant compte des deux formats possibles)
      const hasDate = Array.from(personDates).some(d => {
        const normalizedDate = d.includes('T') ? d.split('T')[0] : d;
        return normalizedDate === normalizedDateStr;
      });
      
      if (hasDate) {
        // Supprimer toutes les versions de cette date (normalisée ou non)
        Array.from(personDates).forEach(d => {
          const normalizedD = d.includes('T') ? d.split('T')[0] : d;
          if (normalizedD === normalizedDateStr) {
            personDates.delete(d);
          }
        });
      } else {
        // Ajouter la date normalisée
        personDates.add(normalizedDateStr);
      }
      
      newSelections.set(personId, personDates);
      return newSelections;
    });
  };

  const handlePersonnelSelection = (personId) => {
    setSelectedPersonnel(prevSelected => {
      if (prevSelected.includes(personId)) {
        // Désélectionner le personnel et supprimer ses sélections de dates et horaires
        setPersonnelSelections(prev => {
          const newMap = new Map(prev);
          newMap.delete(personId);
          return newMap;
        });
        setPersonnelTimeSchedules(prev => {
          const newMap = new Map(prev);
          newMap.delete(personId);
          return newMap;
        });
        return prevSelected.filter(id => id !== personId);
      } else {
        // Sélectionner le personnel et initialiser ses horaires avec les horaires globaux
        setPersonnelTimeSchedules(prev => {
          const newMap = new Map(prev);
          newMap.set(personId, { ...globalTimeSchedule });
          return newMap;
        });
        return [...prevSelected, personId];
      }
    });
  };

  const handleColumnSelection = (date) => {
    // Normaliser le format de la date (YYYY-MM-DD) avec notre fonction utilitaire
    const normalizedDateStr = normalizeDateFormat(date);
    
    // Si aucun personnel n'est sélectionné, sélectionner automatiquement tout le personnel
    if (selectedPersonnel.length === 0) {
      setSelectedPersonnel(personnel.map(p => p.id));
    }
    
    // Créer un ensemble de dates globales sélectionnées pour l'étape 2
    const globalSelectedDates = new Set();
    periodDays.forEach(day => {
      const dayStr = normalizeDateFormat(day);
      // Vérifier si cette date est déjà sélectionnée pour au moins un membre du personnel
      const isSelected = Array.from(personnelSelections.values()).some(personDates => {
        if (personDates instanceof Set) {
          return Array.from(personDates).some(d => {
            const normalizedDate = normalizeDateFormat(d);
            return normalizedDate === dayStr;
          });
        }
        return false;
      });
      if (isSelected) {
        globalSelectedDates.add(dayStr);
      }
    });
    
    // Vérifier si la date cliquée est déjà sélectionnée globalement
    const isDateSelected = globalSelectedDates.has(normalizedDateStr);
    
    setPersonnelSelections(prev => {
      const newSelections = new Map(prev);
      
      // Sélectionner tous les membres du personnel s'ils ne sont pas déjà sélectionnés
      personnel.forEach(person => {
        if (!newSelections.has(person.id)) {
          newSelections.set(person.id, new Set());
        }
      });
      
      // Basculer la sélection pour tous les personnels sélectionnés
      selectedPersonnel.forEach(personId => {
        const personDates = newSelections.get(personId) || new Set();
        
        if (isDateSelected) {
          // Désélectionner la date pour tous
          Array.from(personDates).forEach(d => {
            const normalizedD = normalizeDateFormat(d);
            if (normalizedD === normalizedDateStr) {
              personDates.delete(d);
            }
          });
        } else {
          // Sélectionner la date pour tous
          personDates.add(normalizedDateStr);
        }
        
        newSelections.set(personId, personDates);
      });
      
      return newSelections;
    });
  };

  const handleRowSelection = (personId) => {
    setPersonnelSelections(prev => {
      const newSelections = new Map(prev);
      const personDates = newSelections.get(personId) || new Set();
      
      // Normaliser les dates de la période avec notre fonction utilitaire
      const normalizedPeriodDays = periodDays.map(date => normalizeDateFormat(date));
      
      // Vérifier si tous les jours sont déjà sélectionnés
      const allDatesSelected = normalizedPeriodDays.every(normalizedDateStr => {
        return Array.from(personDates).some(d => {
          const normalizedDate = normalizeDateFormat(d);
          return normalizedDate === normalizedDateStr;
        });
      });
      
      if (allDatesSelected) {
        // Si tous les jours sont déjà sélectionnés, désélectionner tous
        personDates.clear();
      } else {
        // Sinon, sélectionner tous les jours normalisés
        normalizedPeriodDays.forEach(normalizedDateStr => {
          personDates.add(normalizedDateStr);
        });
      }
      
      newSelections.set(personId, personDates);
      return newSelections;
    });
  };

  const handlePeriodChange = (event) => {
    const periodId = event.target.value;
    setSelectedPeriod(periodId);
    setPersonnelSelections(new Map());
    
    const selectedVacation = vacationPeriods.find(period => period.id === periodId);

    if (selectedVacation) {
      // Utiliser les champs originaux pour la compatibilité
      setSelectedPeriodDates({
        debut: selectedVacation.debut,
        fin: selectedVacation.fin
      });
      
      // Convertir les timestamps en Date pour generateDaysBetweenDates
      let debutDate, finDate;
      
      // Gestion sécurisée de la date de début
      try {
        if (selectedVacation.debut && typeof selectedVacation.debut.toDate === 'function') {
          debutDate = selectedVacation.debut.toDate();
        } else if (selectedVacation.debutDate) {
          debutDate = selectedVacation.debutDate;
        } else {
          debutDate = new Date(selectedVacation.debut);
        }
      } catch (error) {
        console.error('Erreur lors de la conversion de la date de début:', error);
        debutDate = new Date(); // Fallback à la date actuelle
      }
      
      // Gestion sécurisée de la date de fin
      try {
        if (selectedVacation.fin && typeof selectedVacation.fin.toDate === 'function') {
          finDate = selectedVacation.fin.toDate();
        } else if (selectedVacation.finDate) {
          finDate = selectedVacation.finDate;
        } else {
          finDate = new Date(selectedVacation.fin);
        }
      } catch (error) {
        console.error('Erreur lors de la conversion de la date de fin:', error);
        finDate = new Date(); // Fallback à la date actuelle
      }
      
      // Vérifier que les dates sont valides avant de générer les jours
      if (isNaN(debutDate.getTime()) || isNaN(finDate.getTime())) {
        console.error('Dates invalides après conversion:', { debutDate, finDate });
        setError('Erreur: Dates de période invalides');
        setPeriodDays([]);
      } else {
        // Réinitialiser les heures à minuit pour éviter les problèmes de fuseau horaire
        debutDate.setHours(0, 0, 0, 0);
        finDate.setHours(0, 0, 0, 0);
        
        
        // Générer les jours avec notre fonction améliorée qui préserve les jours locaux
        const days = generateDaysBetweenDates(debutDate, finDate);
        
        // Afficher les jours générés pour débogage
        
        setPeriodDays(days);
      }
    } else {
      setSelectedPeriodDates(null);
      setPeriodDays([]);
    }
  };

  const handleOpenCreateDialog = () => {
    setEditMode(false);
    setSelectedPermanenceToEdit(null);
    setIsEditingWholePeriod(false);
    setOpenViewDialog(true);
  };
  
  // Fonction pour ouvrir le dialogue de modification générale (toute la période)
  const handleOpenEditDialogGeneral = (periodPermanences, periodId) => {
    setEditMode(true);
    setIsEditingWholePeriod(true); // Activer le flag de modification générale
    // Pour la modification générale, on utilise la première permanence comme référence
    setSelectedPermanenceToEdit(periodPermanences[0]);
    
    // Récupérer la période
    setSelectedPeriod(periodId);
    
    
    // Réinitialiser les sélections avant de les configurer
    setPersonnelSelections(new Map());
    
    // Récupérer les horaires de la première permanence (généralement identiques)
    if (periodPermanences[0].startTime && periodPermanences[0].endTime) {
      setGlobalTimeSchedule({
        startTime: periodPermanences[0].startTime,
        endTime: periodPermanences[0].endTime
      });
    }
    
    // Trouver la période dans les données
    const selectedVacation = vacationPeriods.find(period => period.id === periodId);
    
    if (selectedVacation) {
      // Configurer les dates de la période
      setSelectedPeriodDates({
        debut: selectedVacation.debut,
        fin: selectedVacation.fin
      });
      
      // Générer les jours de la période
      let debutDate, finDate;
      
      try {
        if (selectedVacation.debut && typeof selectedVacation.debut.toDate === 'function') {
          debutDate = selectedVacation.debut.toDate();
        } else if (selectedVacation.debutDate) {
          debutDate = selectedVacation.debutDate;
        } else {
          debutDate = new Date(selectedVacation.debut);
        }
      } catch (error) {
        console.error('Erreur lors de la conversion de la date de début:', error);
        debutDate = new Date();
      }
      
      try {
        if (selectedVacation.fin && typeof selectedVacation.fin.toDate === 'function') {
          finDate = selectedVacation.fin.toDate();
        } else if (selectedVacation.finDate) {
          finDate = selectedVacation.finDate;
        } else {
          finDate = new Date(selectedVacation.fin);
        }
      } catch (error) {
        console.error('Erreur lors de la conversion de la date de fin:', error);
        finDate = new Date();
      }
      
      if (!isNaN(debutDate.getTime()) && !isNaN(finDate.getTime())) {
        const days = generateDaysBetweenDates(debutDate, finDate);
        setPeriodDays(days);
        
        // Collecter tous les personnels de toutes les permanences avec leurs informations
        const personnelMap = new Map();
        periodPermanences.forEach(perm => {
          perm.personnel.forEach(personId => {
            if (!personnelMap.has(personId) && perm.personnelDetails) {
              // Trouver les détails du personnel dans la permanence
              const personDetails = perm.personnelDetails.find(p => p.id === personId);
              if (personDetails) {
                personnelMap.set(personId, personDetails);
              }
            }
          });
        });
        
        
        // Trier les personnels par ordre alphabétique
        const sortedPersonnelIds = Array.from(personnelMap.keys()).sort((a, b) => {
          const personA = personnelMap.get(a);
          const personB = personnelMap.get(b);
          
          if (!personA || !personB) return 0;
          
          const lastNameA = (personA.lastName || '').toLowerCase();
          const lastNameB = (personB.lastName || '').toLowerCase();
          
          if (lastNameA < lastNameB) return -1;
          if (lastNameA > lastNameB) return 1;
          
          const firstNameA = (personA.firstName || '').toLowerCase();
          const firstNameB = (personB.firstName || '').toLowerCase();
          
          if (firstNameA < firstNameB) return -1;
          if (firstNameB > firstNameA) return 1;
          
          return 0;
        });
        
        
        setSelectedPersonnel(sortedPersonnelIds);
        
        // Configurer les sélections de dates pour chaque personnel
        
        // Créer une nouvelle Map pour forcer la mise à jour du state
        const newSelections = new Map();
        const newPersonnelTimeSchedules = new Map();
        
        // Pour chaque permanence, récupérer les dates de chaque personnel
        periodPermanences.forEach(permanence => {
          permanence.personnel.forEach(personId => {
            // Initialiser le Set de dates pour ce personnel s'il n'existe pas
            if (!newSelections.has(personId)) {
              newSelections.set(personId, new Set());
            }
            
            // Initialiser les horaires pour ce personnel
            if (permanence.startTime && permanence.endTime) {
              newPersonnelTimeSchedules.set(personId, {
                startTime: permanence.startTime,
                endTime: permanence.endTime
              });
            }
            
            const dateSet = newSelections.get(personId);
            
            // Ajouter les dates de cette permanence au Set du personnel
            if (Array.isArray(permanence.days)) {
              permanence.days.forEach(day => {
                const normalizedDay = normalizeDateFormat(day);
                if (normalizedDay) {
                  dateSet.add(normalizedDay);
                }
              });
            } else if (permanence.days && typeof permanence.days === 'object') {
              try {
                Object.values(permanence.days).forEach(day => {
                  const normalizedDay = normalizeDateFormat(day);
                  if (normalizedDay) {
                    dateSet.add(normalizedDay);
                  }
                });
              } catch (err) {
                console.error('Erreur lors de la conversion des jours:', err);
              }
            }
            
          });
        });
        
        // Mettre à jour l'état avec la nouvelle Map
        setPersonnelSelections(newSelections);
        setPersonnelTimeSchedules(newPersonnelTimeSchedules);
        
        // Configurer les observations (prendre celles de la première permanence)
        setObservations(periodPermanences[0].observations || '');
      }
    }
    
    setOpenViewDialog(true);
  };

  const handleOpenEditDialog = (permanence) => {
    setEditMode(true);
    setIsEditingWholePeriod(false); // Désactiver le flag de modification générale (modification individuelle)
    setSelectedPermanenceToEdit(permanence);
    
    // Récupérer la période de la permanence
    const periodId = permanence.period;
    setSelectedPeriod(periodId);
    
    
    // Réinitialiser les sélections avant de les configurer
    setPersonnelSelections(new Map());
    
    // Récupérer les horaires de la permanence
    if (permanence.startTime && permanence.endTime) {
      setGlobalTimeSchedule({
        startTime: permanence.startTime,
        endTime: permanence.endTime
      });
      
      // Initialiser les horaires personnels
      const newPersonnelTimeSchedules = new Map();
      permanence.personnel.forEach(personId => {
        newPersonnelTimeSchedules.set(personId, {
          startTime: permanence.startTime,
          endTime: permanence.endTime
        });
      });
      setPersonnelTimeSchedules(newPersonnelTimeSchedules);
    }
    
    // Trouver la période dans les données
    const selectedVacation = vacationPeriods.find(period => period.id === periodId);
    
    if (selectedVacation) {
      // Configurer les dates de la période
      setSelectedPeriodDates({
        debut: selectedVacation.debut,
        fin: selectedVacation.fin
      });
      
      // Générer les jours de la période
      let debutDate, finDate;
      
      try {
        if (selectedVacation.debut && typeof selectedVacation.debut.toDate === 'function') {
          debutDate = selectedVacation.debut.toDate();
        } else if (selectedVacation.debutDate) {
          debutDate = selectedVacation.debutDate;
        } else {
          debutDate = new Date(selectedVacation.debut);
        }
      } catch (error) {
        console.error('Erreur lors de la conversion de la date de début:', error);
        debutDate = new Date();
      }
      
      try {
        if (selectedVacation.fin && typeof selectedVacation.fin.toDate === 'function') {
          finDate = selectedVacation.fin.toDate();
        } else if (selectedVacation.finDate) {
          finDate = selectedVacation.finDate;
        } else {
          finDate = new Date(selectedVacation.fin);
        }
      } catch (error) {
        console.error('Erreur lors de la conversion de la date de fin:', error);
        finDate = new Date();
      }
      
      if (!isNaN(debutDate.getTime()) && !isNaN(finDate.getTime())) {
        const days = generateDaysBetweenDates(debutDate, finDate);
        setPeriodDays(days);
        
        // Sélectionner le personnel de la permanence
        setSelectedPersonnel(permanence.personnel);
        
        // Configurer les sélections de dates
        
        // Créer une nouvelle Map pour forcer la mise à jour du state
        const newSelections = new Map();
        
        // Traiter chaque membre du personnel
        permanence.personnel.forEach(personId => {
          // Créer un nouveau Set pour stocker les dates normalisées
          const dateSet = new Set();
          
          // S'assurer que days est correctement traité selon son type
          if (Array.isArray(permanence.days)) {
            // Si days est déjà un tableau, normaliser chaque date et l'ajouter au Set
            permanence.days.forEach(day => {
              const normalizedDay = normalizeDateFormat(day);
              if (normalizedDay) {
                dateSet.add(normalizedDay);
              }
            });
          } else if (permanence.days && typeof permanence.days === 'object') {
            try {
              // Si days est un objet, convertir ses valeurs en tableau
              Object.values(permanence.days).forEach(day => {
                const normalizedDay = normalizeDateFormat(day);
                if (normalizedDay) {
                  dateSet.add(normalizedDay);
                }
              });
            } catch (err) {
              console.error('Erreur lors de la conversion des jours en tableau:', err);
            }
          } else if (typeof permanence.days === 'string') {
            // Si days est une chaîne unique
            const normalizedDay = normalizeDateFormat(permanence.days);
            if (normalizedDay) {
              dateSet.add(normalizedDay);
            }
          }
          
          
          // Ajouter le Set de dates au Map des sélections
          newSelections.set(personId, dateSet);
        });
        
        // Mettre à jour l'état avec la nouvelle Map
        setPersonnelSelections(newSelections);
        
        // Configurer les observations
        setObservations(permanence.observations || '');
      }
    }
    
    setOpenViewDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (!selectedPeriod) {
        throw new Error("Veuillez sélectionner une période de vacances");
      }

      if (selectedPersonnel.length === 0) {
        throw new Error("Veuillez sélectionner au moins un membre du personnel");
      }

      // Optimisation : Préparer les données du personnel en une seule fois
      const personnelInfoMap = new Map();
      personnel.forEach(person => {
        personnelInfoMap.set(person.id, person);
      });

      // Créer une permanence pour chaque personnel sélectionné
      const permanencesToCreate = selectedPersonnel.map(personId => {
        // Récupérer les dates sélectionnées pour ce personnel
        const selectedDates = personnelSelections.get(personId);
        
        // Normaliser toutes les dates au format YYYY-MM-DD avec notre fonction utilitaire
        const personDates = selectedDates ? Array.from(selectedDates).map(date => normalizeDateFormat(date)) : [];
        
        // Récupérer les horaires pour ce personnel ou utiliser les horaires globaux
        const timeSchedule = useGlobalTimeSchedule 
          ? globalTimeSchedule 
          : personnelTimeSchedules.get(personId) || globalTimeSchedule;
        
        // Récupérer les informations du personnel depuis notre Map
        const personInfo = personnelInfoMap.get(personId) || {};
        
        // Déterminer si cette personne est un chef de service
        const isChefService = personInfo.isChefService || false;
        
        return {
          period: selectedPeriod,
          personnel: [personId],
          personnelName: `${personInfo.firstName || ''} ${personInfo.lastName || ''}`.trim(),
          days: personDates,
          startTime: timeSchedule.startTime,
          endTime: timeSchedule.endTime,
          observations,
          establishmentId: userData.establishmentId,
          schoolYear: currentYear,
          createdAt: serverTimestamp(), // Utiliser serverTimestamp pour une meilleure cohérence
          isChefService: isChefService,
          department: personInfo.department || ''
        };
      }).filter(p => p.days.length > 0);

      // En mode création, vérifier qu'au moins une date est sélectionnée
      if (!editMode && permanencesToCreate.length === 0) {
        throw new Error("Veuillez sélectionner au moins une date pour un personnel");
      }

      // Préparation des données du personnel en Map pour accès rapide
      const personnelMap = new Map();
      personnel.forEach(person => {
        personnelMap.set(person.id, person);
      });
      
      if (editMode && selectedPermanenceToEdit) {
        if (isEditingWholePeriod) {
          // Mode modification GÉNÉRALE : supprimer toutes les anciennes permanences de la période et créer les nouvelles
          
          // 1. Récupérer toutes les permanences existantes pour cette période
          const permanencesRef = collection(db, 'permanences');
          const q = query(
            permanencesRef,
            where('establishmentId', '==', userData.establishmentId),
            where('period', '==', selectedPeriod)
          );
          const existingPermanencesSnapshot = await getDocs(q);
          
          
          // 2. Utiliser un batch pour supprimer toutes les anciennes permanences
          const deleteBatch = writeBatch(db);
          existingPermanencesSnapshot.docs.forEach(docSnapshot => {
            deleteBatch.delete(doc(db, 'permanences', docSnapshot.id));
          });
          await deleteBatch.commit();
          
          // 3. Créer les nouvelles permanences avec les données mises à jour
          
          const createBatch = writeBatch(db);
          
          for (const personId of selectedPersonnel) {
            const person = personnelMap.get(personId);
            const isChefService = person?.role === 'chefService';
            
            // Récupérer les dates sélectionnées spécifiques à ce personnel
            const personSelectedDates = Array.from(personnelSelections.get(personId) || []).map(date => normalizeDateFormat(date));
            
            // Vérifier si ce personnel a des dates sélectionnées
            if (personSelectedDates.length === 0) {
              continue;
            }

            const permanenceData = {
              establishmentId: userData.establishmentId,
              period: selectedPeriod,
              personnel: [personId],
              days: personSelectedDates,
              startTime: globalTimeSchedule.startTime,
              endTime: globalTimeSchedule.endTime,
              observations: observations,
              isChefService: isChefService,
              visibleToPersonnel: visibleToPersonnel,
              createdAt: serverTimestamp(),
              createdBy: userData.uid
            };
            
            const permanenceRef = doc(collection(db, 'permanences'));
            createBatch.set(permanenceRef, permanenceData);
          }
          
          await createBatch.commit();
        } else {
          // Mode modification INDIVIDUELLE : mettre à jour uniquement la permanence sélectionnée
          
          // Récupérer le premier personnel sélectionné
          const personId = selectedPersonnel[0] || selectedPermanenceToEdit.personnel[0];
          const person = personnelMap.get(personId);
          const isChefService = person?.role === 'chefService';
          
          // Récupérer les dates sélectionnées pour ce personnel
          const personSelectedDates = Array.from(personnelSelections.get(personId) || []).map(date => normalizeDateFormat(date));
          
          
          // Mettre à jour uniquement cette permanence
          await updateDoc(doc(db, 'permanences', selectedPermanenceToEdit.id), {
            personnel: selectedPersonnel,
            days: personSelectedDates,
            startTime: globalTimeSchedule.startTime,
            endTime: globalTimeSchedule.endTime,
            observations: observations,
            isChefService: isChefService,
            visibleToPersonnel: visibleToPersonnel,
            updatedAt: serverTimestamp()
          });
          
        }
      } else {
        // Mode création : ajouter de nouvelles permanences
        const batch = writeBatch(db);
        
        for (const personId of selectedPersonnel) {
          const person = personnelMap.get(personId);
          const isChefService = person?.role === 'chefService';
          
          // Récupérer les dates sélectionnées spécifiques à ce personnel
          const personSelectedDates = Array.from(personnelSelections.get(personId) || []).map(date => normalizeDateFormat(date));
          
          // Vérifier si ce personnel a des dates sélectionnées
          if (personSelectedDates.length === 0) {
            continue;
          }

          const permanenceData = {
            establishmentId: userData.establishmentId,
            period: selectedPeriod,
            personnel: [personId],
            days: personSelectedDates,
            startTime: globalTimeSchedule.startTime,
            endTime: globalTimeSchedule.endTime,
            observations: observations,
            isChefService: isChefService,
            visibleToPersonnel: visibleToPersonnel,
            createdAt: serverTimestamp(),
            createdBy: userData.uid
          };
          
          const permanenceRef = doc(collection(db, 'permanences'));
          batch.set(permanenceRef, permanenceData);
        }
        
        // Exécution du batch
        await batch.commit();
      }

      // Vider les caches pour forcer le rechargement des données
      setPeriodsCache(new Map());
      setPersonnelCache(new Map());
      
      // Recharger toutes les permanences pour s'assurer que les modifications sont visibles
      await fetchPermanences();
      
      // Définition de la fonction resetForm
      const resetForm = () => {
        setSelectedPeriod('');
        setSelectedPersonnel([]);
        setObservations('');
        setGlobalTimeSchedule({ startTime: '08:00', endTime: '17:00' });
        setPersonnelSelections(new Map());
        setVisibleToPersonnel(true);
        setSelectedPeriodDates(null);
        setPeriodDays([]);
      };
      
      if (editMode && selectedPermanenceToEdit) {
        setEditMode(false);
        setSelectedPermanenceToEdit(null);
        setIsEditingWholePeriod(false);
        setOpenViewDialog(false); // Fermer la boîte de dialogue
        showSnackbar('Permanence modifiée avec succès', 'success');
      } else {
        setOpenViewDialog(false); // Fermer la boîte de dialogue
        showSnackbar('Permanences créées avec succès', 'success');
      }

      // Réinitialisation du formulaire
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la création/modification des permanences:', error);
      showSnackbar(error.message || 'Erreur lors de la création/modification des permanences', 'error');
    }
  };

  // Fonction pour rendre une cellule de date (supprimée car intégrée directement dans le rendu du tableau)
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Section des permanences existantes */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Permanences existantes
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenViewDialog(true)}
        >
          Créer une nouvelle permanence
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Typography>Chargement des permanences...</Typography>
        </Box>
      ) : permanences.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Aucune permanence n'a encore été créée.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {groupPermanencesByPeriod(permanences).map(({ periodId, periodName, permanences: periodPermanences }) => (
            <Paper key={periodId} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    {periodName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#e8f5e9', px: 2, py: 0.5, borderRadius: 1, border: '1px solid #c8e6c9' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={periodPermanences[0]?.visibleToPersonnel || false}
                          onChange={(e) => {
                            // Mettre à jour l'état local immédiatement pour un feedback visuel instantané
                            const newVisibility = e.target.checked;
                            
                            // Créer une copie profonde des permanences pour mise à jour locale
                            setPermanences(prev => {
                              return prev.map(p => {
                                if (p.period === periodId) {
                                  return { ...p, visibleToPersonnel: newVisibility };
                                }
                                return p;
                              });
                            });
                            
                            // Mettre à jour la visibilité de toutes les permanences de cette période dans Firestore
                            const batch = writeBatch(db);
                            periodPermanences.forEach(permanence => {
                              const permanenceRef = doc(db, 'permanences', permanence.id);
                              batch.update(permanenceRef, { visibleToPersonnel: newVisibility });
                            });
                            
                            // Exécuter le batch
                            batch.commit()
                              .then(() => {
                                showSnackbar(`Visibilité des permanences de ${periodName} mise à jour`, 'success');
                              })
                              .catch(error => {
                                console.error("Erreur lors de la mise à jour de la visibilité:", error);
                                showSnackbar("Erreur lors de la mise à jour de la visibilité", 'error');
                                
                                // En cas d'erreur, rétablir l'état précédent
                                setPermanences(prev => {
                                  return prev.map(p => {
                                    if (p.period === periodId) {
                                      return { ...p, visibleToPersonnel: !newVisibility };
                                    }
                                    return p;
                                  });
                                });
                              });
                          }}
                          color="primary"
                          size="small"
                        />
                      }
                      label="Visible pour le personnel"
                    />
                    <Tooltip title="Cochez cette case pour rendre les permanences de cette période visibles dans les comptes personnel">
                      <IconButton size="small">
                        <InfoIcon fontSize="small" color="info" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteConfirmDialog({ 
                      open: true, 
                      periodId: periodId, 
                      periodName: periodName 
                    })}
                    disabled={loading}
                  >
                    Supprimer
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PdfIcon />}
                    onClick={() => {
                      // S'assurer que toutes les données nécessaires sont disponibles
                      if (!periodName || !periodPermanences || !personnel) {
                        showSnackbar('Données insuffisantes pour générer le PDF', 'error');
                        return;
                      }
                      
                      setSelectedPeriodForPdf({ 
                        periodName, 
                        permanences: periodPermanences,
                        personnel: personnel,
                        anneeScolaire: establishmentInfo?.anneeScolaire || '',
                        responsableEncadrement: establishmentInfo?.responsableEncadrement || '',
                        observations: periodPermanences[0]?.observations || ''
                      });
                      // Augmenter le délai pour s'assurer que le composant PDF est bien rendu
                      setTimeout(handlePrint, 500);
                    }}
                    disabled={loading}
                  >
                    Générer PDF
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenEditDialogGeneral(periodPermanences, periodId)}
                    disabled={loading}
                  >
                    Modifier
                  </Button>
                </Box>
              </Box>
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 200 }} rowSpan={2}>Nom et Prénom</TableCell>
                      <TableCell sx={{ minWidth: 150 }} rowSpan={2}>Grade</TableCell>
                      <TableCell sx={{ minWidth: 150 }} rowSpan={2}>Service</TableCell>
                      {organizeDataForTable(periodPermanences, personnel).dates.map((date, index) => (
                        <TableCell 
                          key={`day-${date}`}
                          align="center"
                          sx={{ 
                            minWidth: 80,
                            backgroundColor: index % 2 === 0 ? 'background.paper' : 'action.hover',
                            borderBottom: 'none',
                            color: 'primary.main',
                            fontWeight: 'bold'
                          }}
                        >
                          {getShortDayName(date)}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      {organizeDataForTable(periodPermanences, personnel).dates.map((date, index) => (
                        <TableCell 
                          key={`date-${date}`}
                          align="center"
                          sx={{ 
                            minWidth: 80,
                            backgroundColor: index % 2 === 0 ? 'background.paper' : 'action.hover'
                          }}
                          title={getDayName(date)}
                        >
                          {formatShortDate(new Date(date))}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {organizeDataForTable(periodPermanences, personnel).personnel.map((row) => {
                      // Trouver la permanence correspondante à cette personne
                      const personPermanence = periodPermanences.find(p => 
                        p.personnel.includes(row.person.id)
                      );
                      
                      return (
                        <TableRow key={row.person.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                            sx={{ 
                              fontWeight: row.person.isChefService ? 'bold' : 'normal',
                              color: row.person.isChefService ? 'primary.main' : 'inherit'
                            }}
                          >
                            {row.person.lastName} {row.person.firstName}
                            {row.person.isChefService && (
                              <Chip 
                                label="Chef de service" 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {personPermanence?.startTime} - {personPermanence?.endTime}
                          </Typography>
                              {personPermanence && (
                                <Tooltip title="Modifier cette permanence">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleOpenEditDialog(personPermanence)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{row.person.grade || '-'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2">{row.person.service || row.person.department || '-'}</Typography>
                              {/* Afficher si c'est un chef de service */}
                              {row.person.isChefService && (
                                <Typography variant="caption" color="primary">
                                  Chef de service - Département: {row.person.department || 'Non spécifié'}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          {row.dates.map((isPresent, index) => (
                            <TableCell 
                              key={index} 
                              align="center"
                              sx={{
                                backgroundColor: index % 2 === 0 ? 'background.paper' : 'action.hover',
                                color: isPresent ? 'success.main' : 'inherit',
                                fontWeight: isPresent ? 'bold' : 'normal'
                              }}
                            >
                              {isPresent ? '✓' : ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Box>
      )}

      {/* Dialog pour créer ou modifier une permanence */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="xl"
        fullWidth
>
  <DialogTitle>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h6">
        {editMode ? 'Modifier la permanence' : 'Créer une nouvelle permanence'}
      </Typography>
      <IconButton onClick={() => setOpenViewDialog(false)} size="small">
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>
  <DialogContent>
    {/* Étape 1: Sélection de la période de vacances */}
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
        Étape 1: Sélection de la période de vacances
      </Typography>
      <TextField
        select
        fullWidth
        value={selectedPeriod}
        onChange={handlePeriodChange}
      >
        {vacationPeriods.map((period) => (
          <MenuItem key={period.id} value={period.id}>
            {period.nom}
          </MenuItem>
        ))}
      </TextField>
    </Box>

    {selectedPeriodDates && (
      <>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
          Du {formatDate(selectedPeriodDates.debut)} au {formatDate(selectedPeriodDates.fin)}
        </Typography>

        {/* Étape 2: Sélection des jours de permanences */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
            Étape 2: Sélection des jours de permanences
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Cochez les dates qui seront des jours de permanence pendant cette période.
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {periodDays.map((date) => {
              // Utiliser normalizeDateFormat pour préserver le jour local
              const dateStr = normalizeDateFormat(date);
              const jourFerie = isJourFerie(date);
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              
              // Vérifier si cette date est sélectionnée pour au moins un membre du personnel
              const isDateSelected = Array.from(personnelSelections.values()).some(personDates => {
                if (personDates instanceof Set) {
                  return Array.from(personDates).some(d => {
                    // Utiliser normalizeDateFormat pour une comparaison cohérente
                    const normalizedDate = normalizeDateFormat(d);
                    return normalizedDate === dateStr;
                  });
                }
                return false;
              });
              
              return (
                <Chip
                  key={dateStr}
                  label={`${getShortDayName(date)} ${formatShortDate(date)}`}
                  onClick={() => {
                    // Sélectionner/désélectionner cette date pour tous les membres du personnel sélectionnés
                    // Utiliser normalizeDateFormat au lieu de toISOString pour préserver le jour local
                    handleColumnSelection(normalizeDateFormat(date));
                  }}
                  clickable
                  variant={isDateSelected ? "filled" : "outlined"}
                  icon={isDateSelected ? <CheckCircleIcon fontSize="small" /> : null}
                  sx={{
                    backgroundColor: isDateSelected 
                      ? '#1976d2' 
                      : (isWeekend ? '#f5f5f5' : (jourFerie ? jourFerie.couleur : '#e0e0e0')),
                    color: isDateSelected ? 'white' : 'inherit',
                    border: isDateSelected ? 'none' : '1px solid #bdbdbd',
                    '&:hover': {
                      backgroundColor: isDateSelected ? '#1565c0' : '#d5d5d5',
                      transform: 'scale(1.05)',
                      transition: 'transform 0.2s',
                    },
                    cursor: 'pointer',
                    fontWeight: isDateSelected ? 'bold' : 'normal',
                    transition: 'all 0.2s',
                  }}
                />
              );
            })}
          </Box>
          
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
            Étape 3: Sélection du personnel et des dates
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Sélectionnez le personnel et cochez les dates qui les concernent.
          </Typography>
          
          {/* Horaires globaux pour toutes les permanences */}
          <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Horaires des permanences
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Heure de début"
                type="time"
                value={globalTimeSchedule.startTime}
                onChange={(e) => setGlobalTimeSchedule({ ...globalTimeSchedule, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                size="small"
              />
              <Typography variant="body2">à</Typography>
              <TextField
                label="Heure de fin"
                type="time"
                value={globalTimeSchedule.endTime}
                onChange={(e) => setGlobalTimeSchedule({ ...globalTimeSchedule, endTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                size="small"
              />
            </Box>
          </Box>
          
          {/* Note: La case à cocher de visibilité a été déplacée vers le panneau des permanences existantes */}
          
          {/* Note sur les chefs de service */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary">
              Les chefs de service sont inclus dans la liste du personnel et sont identifiés par une étiquette spéciale.
            </Typography>
          </Box>
          
          {/* Barre de recherche pour filtrer le personnel */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Rechercher un membre du personnel"
              placeholder="Nom, prénom, grade, service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ color: 'action.active', mr: 1 }}>
                    🔍
                  </Box>
                ),
                endAdornment: searchTerm ? (
                  <IconButton
                    size="small"
                    aria-label="clear search"
                    onClick={() => setSearchTerm('')}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null
              }}
            />
            <Button 
              variant="outlined" 
              color="primary" 
              size="small"
              onClick={() => setSearchTerm('')}
              disabled={!searchTerm}
            >
              Réinitialiser
            </Button>
          </Box>
          
          <TableContainer sx={{ maxHeight: 440, border: '1px solid #e0e0e0', borderRadius: '4px', overflowX: 'auto' }}>

            <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedPersonnel.length > 0 && selectedPersonnel.length < personnel.length}
                      checked={personnel.length > 0 && selectedPersonnel.length === personnel.length}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedPersonnel(personnel.map(p => p.id));
                        } else {
                          setSelectedPersonnel([]);
                          setPersonnelSelections(new Map());
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 50, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Actions</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Nom et Prénom</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Grade</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Service</TableCell>
                  {periodDays.map((date, index) => {
                    const formattedDate = date.toLocaleDateString('fr-FR', { 
                      weekday: 'short', 
                      day: 'numeric'
                    });
                    
                    const jourFerie = isJourFerie(date);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    
                    return (
                      <TableCell 
                        key={date.toISOString()} 
                        align="center"
                        onClick={() => handleColumnSelection(date)}
                        title={jourFerie ? `${jourFerie.nom}` : 'Cliquer pour sélectionner/désélectionner toute la colonne'}
                        sx={{ 
                          backgroundColor: isWeekend ? '#f5f5f5' : (jourFerie ? jourFerie.couleur : 'inherit'),
                          position: 'relative',
                          minWidth: '80px',
                          padding: '8px 4px',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: isWeekend ? '#eeeeee' : (jourFerie ? jourFerie.couleur : '#f5f5f5'),
                            opacity: 0.8
                          },
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '1px solid #e0e0e0',
                          fontWeight: 'bold'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {formattedDate}
                          </Typography>
                          {jourFerie && (
                            <Chip 
                              label={jourFerie.nom} 
                              size="small" 
                              sx={{ 
                                fontSize: '0.6rem', 
                                height: '16px',
                                maxWidth: '70px',
                                mt: 0.5,
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden'
                              }} 
                            />
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {personnel
                  .filter(person => {
                    if (!searchTerm) return true;
                    const searchLower = searchTerm.toLowerCase();
                    return (
                      person.lastName.toLowerCase().includes(searchLower) ||
                      person.firstName.toLowerCase().includes(searchLower) ||
                      (person.grade && person.grade.toLowerCase().includes(searchLower)) ||
                      (person.service && person.service.toLowerCase().includes(searchLower)) ||
                      (person.department && person.department.toLowerCase().includes(searchLower))
                    );
                  })
                  .map((person) => (
                  <TableRow 
                    key={person.id}
                    sx={{
                      backgroundColor: selectedPersonnel.includes(person.id) 
                        ? 'action.selected' 
                        : 'inherit'
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedPersonnel.includes(person.id)}
                        onChange={() => handlePersonnelSelection(person.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => handleRowSelection(person.id)}
                        disabled={!selectedPersonnel.includes(person.id)}
                      >
                        Tout
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: person.isChefService ? 'bold' : 'normal',
                            color: person.isChefService ? 'primary.main' : 'inherit'
                          }}
                        >
                          {person.lastName} {person.firstName}
                          {person.isChefService && (
                            <Chip 
                              label="Chef de service" 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{person.grade || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">{person.department || '-'}</Typography>
                        {/* Afficher si c'est un chef de service */}
                        {person.isChefService && (
                          <Typography variant="caption" color="primary">
                            Chef de service
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {periodDays.map((date, index) => {
                      // Normaliser le format de la date pour la vérification avec notre fonction utilitaire
                      const dateStr = normalizeDateFormat(date);
                      
                      // Vérifier si la date est sélectionnée en utilisant la fonction isDateInSet
                      const personDates = personnelSelections.get(person.id);
                      let isSelected = isDateInSet(dateStr, personDates);
                      
                      // Ajouter un log pour déboguer
                      if (editMode && selectedPermanenceToEdit && person.id === selectedPermanenceToEdit.personnel[0]) {
                      }
                      
                      const jourFerie = isJourFerie(date);
                      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                      
                      return (
                        <TableCell 
                          key={date.toISOString()} 
                          align="center"
                          sx={{
                            backgroundColor: isSelected 
                              ? '#e3f2fd' 
                              : (isWeekend 
                                ? '#f5f5f5' 
                                : (jourFerie 
                                  ? jourFerie.couleur 
                                  : (index % 2 === 0 ? 'background.paper' : 'action.hover'))),
                            cursor: selectedPersonnel.includes(person.id) ? 'pointer' : 'not-allowed',
                            borderBottom: '1px solid #e0e0e0',
                            borderRight: '1px solid #e0e0e0',
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: selectedPersonnel.includes(person.id) ? '#bbdefb' : undefined,
                            }
                          }}
                          onClick={() => {
                            if (selectedPersonnel.includes(person.id)) {
                              // Passer directement la chaîne de date normalisée
                              handleDateSelection(person.id, dateStr);
                            }
                          }}
                        >
                          <Checkbox
                            disabled={!selectedPersonnel.includes(person.id)}
                            checked={isSelected}
                            onChange={() => {
                              // Passer directement la chaîne de date normalisée
                              handleDateSelection(person.id, dateStr);
                            }}
                            size="small"
                            sx={{
                              color: isSelected ? '#1976d2' : undefined,
                              '&.Mui-checked': {
                                color: '#1976d2',
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.26)',
                              }
                            }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Horaires
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useGlobalTimeSchedule}
                      onChange={(e) => setUseGlobalTimeSchedule(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Utiliser des horaires globaux pour tous les personnels"
                  sx={{ mb: 2 }}
                />
                
                {useGlobalTimeSchedule ? (
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      label="Heure de début"
                      type="time"
                      value={globalTimeSchedule.startTime}
                      onChange={(e) => setGlobalTimeSchedule(prev => ({ ...prev, startTime: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      sx={{ width: '50%' }}
                    />
                    <TextField
                      label="Heure de fin"
                      type="time"
                      value={globalTimeSchedule.endTime}
                      onChange={(e) => setGlobalTimeSchedule(prev => ({ ...prev, endTime: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      sx={{ width: '50%' }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Horaires par personnel
                    </Typography>
                    {selectedPersonnel.map(personId => {
                      const person = personnel.find(p => p.id === personId);
                      const timeSchedule = personnelTimeSchedules.get(personId) || { startTime: '08:00', endTime: '16:00' };
                      
                      return (
                        <Box key={personId} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ width: '30%', fontWeight: 'bold' }}>
                            {person?.lastName} {person?.firstName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, width: '70%' }}>
                            <TextField
                              label="Début"
                              type="time"
                              value={timeSchedule.startTime}
                              onChange={(e) => {
                                setPersonnelTimeSchedules(prev => {
                                  const newMap = new Map(prev);
                                  newMap.set(personId, { ...timeSchedule, startTime: e.target.value });
                                  return newMap;
                                });
                              }}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                              size="small"
                              sx={{ width: '50%' }}
                            />
                            <TextField
                              label="Fin"
                              type="time"
                              value={timeSchedule.endTime}
                              onChange={(e) => {
                                setPersonnelTimeSchedules(prev => {
                                  const newMap = new Map(prev);
                                  newMap.set(personId, { ...timeSchedule, endTime: e.target.value });
                                  return newMap;
                                });
                              }}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                              size="small"
                              sx={{ width: '50%' }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                
                <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
                  Observations
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  placeholder="Ajouter des observations ou commentaires..."
                />
              </Box>
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>
            Annuler
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmit}
            disabled={loading || !selectedPeriod || selectedPersonnel.length === 0}
          >
            {loading ? <CircularProgress size={24} /> : (editMode ? 'Enregistrer' : 'Créer les permanences')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Composant caché pour le PDF */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        {selectedPeriodForPdf && (
          <PdfContent
            ref={pdfComponentRef}
            periodName={selectedPeriodForPdf.periodName}
            permanences={selectedPeriodForPdf.permanences}
            personnel={selectedPeriodForPdf.personnel}
            userData={establishmentInfo}
            observations={selectedPeriodForPdf.observations}
            establishmentName={establishmentInfo.name}
            anneeScolaire={establishmentInfo.anneeScolaire}
            gestionnaire={establishmentInfo.gestionnaire}
            principal={establishmentInfo.principal}
            responsableEncadrement={establishmentInfo.responsableEncadrement}
            logo={establishmentInfo?.logo}
          />
        )}
      </div>

      {/* Légende des jours fériés */}
      <Box sx={{ 
        mt: 2, 
        p: 2, 
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        backgroundColor: '#fff'
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Jours fériés ce mois-ci :
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1 
        }}>
          {Object.entries(joursFeries)
            // Filtrer les jours fériés pour le mois actuellement affiché
            // On extrait le mois à partir du titre de la section (visible dans l'interface)
            .filter(([date]) => {
              // Extraire le mois à partir du format MM-DD
              const month = parseInt(date.split('-')[0], 10);
              
              // Déterminer le mois actuellement affiché (basé sur les données visibles)
              // Si nous sommes en mai (d'après la capture d'écran), month devrait être 5
              return month === 5; // Mai = 5
            })
            .map(([date, { nom, couleur }]) => (
            <Box
              key={date}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: couleur,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.875rem'
              }}
            >
              {nom}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Boîte de dialogue de confirmation de suppression */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ ...deleteConfirmDialog, open: false })}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer toutes les permanences de la période <strong>{deleteConfirmDialog.periodName}</strong> ?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Attention : Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmDialog({ ...deleteConfirmDialog, open: false })}
          >
            Annuler
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => handleDeletePermanence(deleteConfirmDialog.periodId)}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ScheduleManagerComponent;
